import { Add, DeleteOutline, ErrorRounded, Remove } from '@mui/icons-material';
import { Box, Button, CircularProgress } from '@mui/material';
import {
    KeyboardEvent,
    memo,
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';

import { leftButtonStyles, rightButtonStyles } from './QuantityButtonStyles';
import { QuantityInput } from './QuantityInput';

interface OrderItemQuantityPickerProps {
    value: number;
    maxQuantity?: number;
    minQuantity?: number;
    disabled?: boolean;
    showLoading?: boolean;
    showDeleteButton?: boolean;
    showCustomQuantity?: boolean;
    onQuantityChange: (newQuantity: number) => void;
    onDelete?: () => void;
    className?: string;
}

export const OrderItemQuantityPicker = memo(
    ({
        value,
        maxQuantity = 9999,
        minQuantity = 0,
        disabled = false,
        showLoading = false,
        showDeleteButton = false,
        showCustomQuantity = false,
        onQuantityChange,
        onDelete,
        className,
    }: OrderItemQuantityPickerProps) => {
        // Manage the state of the input independently of the value prop
        const [isInputActive, setIsInputActive] = useState(false);
        const [inputValue, setInputValue] = useState('');
        const inputRef = useRef<HTMLInputElement>(null);
        const editingStartValue = useRef(value);

        // Reset input value when value prop changes if not actively editing
        useEffect(() => {
            if (!isInputActive) {
                setInputValue('');
            }
        }, [value, isInputActive]);

        // Determine what buttons should be disabled and what should be shown
        const currentValue = isInputActive
            ? parseInt(inputValue, 10) || 0
            : value;
        const canDecrease = currentValue > minQuantity && !disabled;
        const canIncrease = currentValue < maxQuantity && !disabled;
        const shouldShowDelete =
            showDeleteButton && currentValue === 1 && canDecrease;
        const canChangeInput = (canDecrease || canIncrease) && !disabled;

        // Display value: show input value when active, otherwise show prop value
        const displayValue = isInputActive ? inputValue : value.toString();

        const handleInputUpdate = useCallback(
            (newValue: number) => {
                onQuantityChange(newValue);
                if (isInputActive) {
                    setInputValue(newValue.toString());
                }
            },
            [onQuantityChange, isInputActive],
        );

        const handleIncrease = useCallback(() => {
            if (!canIncrease) return;
            const newValue = currentValue + 1;
            handleInputUpdate(newValue);
        }, [canIncrease, currentValue, handleInputUpdate]);

        const handleDecrease = useCallback(() => {
            if (!canDecrease) return;
            const newValue = currentValue - 1;
            handleInputUpdate(newValue);
        }, [canDecrease, currentValue, handleInputUpdate]);

        const handleDelete = useCallback(() => {
            if (onDelete) {
                onDelete();
            } else {
                handleInputUpdate(0);
            }
        }, [onDelete, handleInputUpdate]);

        const handleInputChange = useCallback((newValue: string) => {
            setInputValue(newValue);
        }, []);

        const handleInputFocus = useCallback(() => {
            setIsInputActive(true);
            editingStartValue.current = value;
            setInputValue(value.toString());
        }, [value]);

        const handleInputBlur = useCallback(() => {
            setIsInputActive(false);

            // If input is empty, revert to original value
            if (!inputValue || inputValue === '') {
                setInputValue('');
                onQuantityChange(editingStartValue.current);
                return;
            }

            const numericValue = parseInt(inputValue, 10);

            // Validate and apply constraints
            let finalValue = Math.max(minQuantity, numericValue);
            finalValue = Math.min(finalValue, maxQuantity);

            if (finalValue !== value) {
                onQuantityChange(finalValue);
            }

            setInputValue('');
        }, [inputValue, value, maxQuantity, onQuantityChange, minQuantity]);

        const handleInputKeyDown = useCallback(
            (event: KeyboardEvent<HTMLInputElement>) => {
                switch (event.key) {
                    case 'ArrowUp':
                        event.preventDefault();
                        handleIncrease();
                        break;
                    case 'ArrowDown':
                        event.preventDefault();
                        handleDecrease();
                        break;
                    case 'Enter':
                    case 'Escape':
                        event.preventDefault();
                        inputRef.current?.blur();
                        break;
                }
            },
            [handleIncrease, handleDecrease],
        );

        const minusButtonConfig = useMemo(() => {
            if (shouldShowDelete) {
                return {
                    icon: <DeleteOutline />,
                    action: handleDelete,
                    disabled: disabled,
                };
            }
            return {
                icon: <Remove />,
                action: handleDecrease,
                disabled: !canDecrease,
            };
        }, [
            shouldShowDelete,
            handleDelete,
            handleDecrease,
            disabled,
            canDecrease,
        ]);

        return (
            <Box
                className={className}
                sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    borderRadius: '4px',
                    backgroundColor: '#FFF',
                    width: 'auto',
                    height: '32px',
                    boxSizing: 'border-box',
                    position: 'relative',
                    opacity: disabled ? 0.6 : 1,
                    pointerEvents: disabled ? 'none' : 'auto',
                }}
            >
                <Button
                    disabled={minusButtonConfig.disabled}
                    onClick={minusButtonConfig.action}
                    sx={leftButtonStyles}
                    data-testid="quantity-decrease-button"
                    disableFocusRipple
                    disableRipple
                >
                    {minusButtonConfig.icon}
                </Button>

                <Box sx={{ position: 'relative', height: '32px' }}>
                    <QuantityInput
                        value={displayValue}
                        onChange={handleInputChange}
                        onKeyDown={handleInputKeyDown}
                        onFocus={handleInputFocus}
                        onBlur={handleInputBlur}
                        disabled={!canChangeInput}
                        inputRef={inputRef}
                    />
                    {showLoading && (
                        <Box
                            sx={{
                                position: 'absolute',
                                top: '50%',
                                left: '50%',
                                transform: 'translate(-50%, -50%)',
                                zIndex: 1,
                                backgroundColor: '#f4f4f4',
                                borderRadius: '4px',
                                padding: '4px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                            }}
                        >
                            <CircularProgress size={16} />
                        </Box>
                    )}
                </Box>

                <Button
                    disabled={!canIncrease}
                    onClick={handleIncrease}
                    sx={rightButtonStyles}
                    data-testid="quantity-increase-button"
                    disableFocusRipple
                    disableRipple
                >
                    <Add />
                </Button>

                {showCustomQuantity && (
                    <Box
                        sx={{
                            position: 'absolute',
                            top: '-9px',
                            right: '-9px',
                            width: '18px',
                            height: '18px',
                            backgroundColor: '#FFF',
                            borderRadius: '50%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            overflow: 'hidden',
                        }}
                    >
                        <ErrorRounded
                            sx={{
                                color: '#3F6EB7',
                                fontSize: '12px',
                            }}
                        />
                    </Box>
                )}
            </Box>
        );
    },
);

OrderItemQuantityPicker.displayName = 'OrderItemQuantityPicker';
